import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { useSession } from '../lib/auth-client.js';
import { useAction, useAsync } from '../lib/useAsync.js';
import { exact, stamp } from '../lib/format.js';
import { STATUS_LABELS, type ReportDetail, type ReportEvidence } from '../lib/reports-api.js';
import { Screen } from '../components/Screen.js';
import { AsyncBoundary, Badge, Button, Field, HoldButton, Note, OutcomeNote, Panel, When, inputStyle } from '../components/ui.js';
import { api } from './reports-shared.js';

/**
 * A report's evidence (support#87, Jon 2026-10-24): what the reporter saw, and
 * the snapshot support took from the app when the report was filed.
 *
 * Text is shown as it is: staff need to read it, and a note is words. An image
 * is NEVER drawn on the report. It opens in its own tab (`EvidenceViewer`),
 * blurred until the person looking chooses to reveal it,
 * through a 60-second link that support records with who opened it and, under a
 * legal hold, why.
 */

const num: CSSProperties = { fontVariantNumeric: 'tabular-nums' };

/** Global Admin holds images under a legal hold (support's `HOLD_CUSTODIANS`). */
export function useIsCustodian(): boolean {
  const { data } = useSession();
  const role = (data?.user as { role?: string | null } | undefined)?.role ?? '';
  return role.split(',').map((r) => r.trim()).includes('global-admin');
}

export const evidencePath = (reportId: string, evidenceId: string) =>
  `#/reports/${encodeURIComponent(reportId)}/evidence/${encodeURIComponent(evidenceId)}`;

/** The ids the app files with a report (keyodo#629), in words. An unknown one shows as sent. */
const REF_LABELS: Record<string, string> = {
  borrowRequestId: 'request',
  tripId: 'trip',
  vehicleId: 'vehicle',
  groupId: 'group',
  photoKey: 'photo',
  field: 'in the',
};
const FIELD_LABELS: Record<string, string> = {
  requestNote: 'request note',
  note: 'note',
  closeoutNote: 'trip closeout note',
  name: 'group name',
  description: 'group description',
};

export function EvidencePanel({ report: r, onChange }: { report: ReportDetail; onChange: (r: ReportDetail) => void }) {
  const capture = useAction();
  // The field reads as the end of the sentence: "request br_5521 in the note".
  const refs = Object.entries(r.refs).sort(([a], [b]) => Number(a === 'field') - Number(b === 'field'));
  const captures = groupByCapture(r.evidence);
  const lastFailure = [...r.events].reverse().find((e) => e.kind === 'SNAPSHOT_FAILED' || e.kind === 'SNAPSHOT_CAPTURED');
  const failed = lastFailure?.kind === 'SNAPSHOT_FAILED' ? String(lastFailure.detail?.code ?? 'unknown') : null;

  const removed = r.contentHiddenAt ? (
    <Badge tone="warn">
      Removed by admin <When iso={r.contentHiddenAt} />
    </Badge>
  ) : null;

  return (
    <Panel title="Reported material" action={removed}>
      <div style={{ padding: '1rem 1.2rem', display: 'grid', gap: '1.1rem' }}>
        {r.contentSnapshot ? (
          <Section title="What the reporter saw">
            <Quote>{r.contentSnapshot}</Quote>
          </Section>
        ) : null}

        {captures.length === 0 ? (
          <Note tone={failed ? 'warn' : undefined}>
            {failed
              ? `Nothing was captured from ${r.product} (${failed}). Capture again, or check that ${r.product} is connected for moderation.`
              : `Nothing has been captured from ${r.product} yet.`}
          </Note>
        ) : (
          captures.map((c, i) => (
            <Section
              key={c.at}
              title={captures.length > 1 ? `Captured from ${r.product}, ${i === captures.length - 1 ? 'latest' : `capture ${i + 1}`}` : `Captured from ${r.product}`}
              aside={
                <time dateTime={c.at} title={exact(c.at)} style={{ fontSize: '.8rem', color: C.textMid2 }}>
                  {stamp(c.at)}
                </time>
              }
            >
              {c.items.map((e) => (
                <EvidenceItem key={e.id} report={r} item={e} />
              ))}
            </Section>
          ))
        )}

        <div style={{ display: 'flex', gap: '.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            disabled={capture.running}
            title="Take a new snapshot from the app. Earlier ones are kept."
            onClick={() =>
              void capture.run(async () => {
                const next = await api.captureEvidence(r.id);
                onChange(next);
                const latest = [...next.events].reverse().find((e) => e.kind === 'SNAPSHOT_CAPTURED' || e.kind === 'SNAPSHOT_FAILED');
                if (latest?.kind === 'SNAPSHOT_FAILED') throw new Error(`The app did not answer (${String(latest.detail?.code)}).`);
                return 'Captured. The earlier snapshot is kept.';
              })
            }
          >
            {capture.running ? 'Capturing…' : 'Capture again'}
          </Button>
          {refs.length ? (
            <span style={{ fontSize: '.8rem', color: C.textMid2, overflowWrap: 'anywhere' }}>
              Where it is in {r.product}:{' '}
              {refs.map(([k, v]) => (
                <span key={k} style={{ marginRight: 12 }}>
                  {REF_LABELS[k] ?? k} <span style={{ ...num, color: C.textMid }}>{k === 'field' ? (FIELD_LABELS[v] ?? v) : v}</span>
                </span>
              ))}
            </span>
          ) : null}
        </div>
        <OutcomeNote outcome={capture.outcome} />
        <DestroyEvidence report={r} onChange={onChange} />
      </div>
    </Panel>
  );
}

function groupByCapture(items: ReportEvidence[]) {
  const map = new Map<string, ReportEvidence[]>();
  for (const e of items) map.set(e.capturedAt, [...(map.get(e.capturedAt) ?? []), e]);
  const order = { CONTENT: 0, IMAGE: 1, PERSON: 2 };
  return [...map.entries()].map(([at, list]) => ({ at, items: list.sort((a, b) => order[a.kind] - order[b.kind]) }));
}

function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ display: 'grid', gap: '.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem' }}>
        <h3 style={{ margin: 0, fontSize: '.86rem', fontWeight: 680, color: C.textMid }}>{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Quote({ children }: { children: ReactNode }) {
  return (
    <blockquote
      style={{
        margin: 0,
        padding: '.75rem 1rem',
        background: C.page,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        whiteSpace: 'pre-wrap',
        lineHeight: 1.55,
        overflowWrap: 'anywhere',
      }}
    >
      {children}
    </blockquote>
  );
}

function EvidenceItem({ report: r, item: e }: { report: ReportDetail; item: ReportEvidence }) {
  if (e.kind === 'CONTENT') {
    const fields = Object.entries((e.data ?? {}) as Record<string, string | null>).filter(([, v]) => v);
    return (
      <div style={{ display: 'grid', gap: '.4rem' }}>
        <Quote>{e.text}</Quote>
        {fields.length > 1
          ? fields.map(([k, v]) => (
              <div key={k} style={{ fontSize: '.86rem', color: C.textMid }}>
                <span style={{ color: C.textMid2 }}>{FIELD_LABELS[k] ?? k}:</span> {v}
              </div>
            ))
          : null}
      </div>
    );
  }
  if (e.kind === 'PERSON') {
    const p = (e.data ?? {}) as { displayName?: string | null; handle?: string | null; details?: Record<string, string | null> };
    const details = Object.entries(p.details ?? {}).filter(([, v]) => v);
    return (
      <div style={{ padding: '.7rem .9rem', border: `1px solid ${C.border}`, borderRadius: 8, display: 'grid', gap: '.25rem' }}>
        <div style={{ fontSize: '.8rem', color: C.textMid2 }}>The reported person, as {r.product} showed them</div>
        <div style={{ fontWeight: 650 }}>
          {p.displayName ?? 'No display name'}
          {p.handle ? <span style={{ fontWeight: 500, color: C.textMid2 }}> · {p.handle}</span> : null}
        </div>
        {details.map(([k, v]) => (
          <div key={k} style={{ fontSize: '.86rem', color: C.textMid, overflowWrap: 'anywhere' }}>
            <span style={{ color: C.textMid2 }}>{k}:</span> {v}
          </div>
        ))}
      </div>
    );
  }
  const label = e.role === 'avatar' ? 'Profile picture' : 'Reported image';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.9rem', flexWrap: 'wrap', padding: '.7rem .9rem', border: `1px dashed ${C.inputBorder}`, borderRadius: 8 }}>
      {e.destroyedAt ? (
        <span style={{ color: C.textMid }}>
          {label} destroyed {stamp(e.destroyedAt)}, at law enforcement request {e.destroyReference}.
        </span>
      ) : (
        <a
          href={evidencePath(r.id, e.id)}
          target="_blank"
          rel="noopener noreferrer"
          title="Opens in a new tab, blurred until you reveal it. The opening is recorded."
          style={{
            padding: '.35rem .7rem',
            fontSize: '.85rem',
            fontWeight: 600,
            borderRadius: 5,
            color: C.brand,
            border: `1px solid ${C.inputBorder}`,
            textDecoration: 'none',
          }}
        >
          {`Open ${label.toLowerCase()}`}
        </a>
      )}
      <span style={{ fontSize: '.8rem', color: C.textMid2 }}>
        {e.contentType?.replace('image/', '').toUpperCase()}
        {e.bytes ? ` · ${Math.max(1, Math.round(e.bytes / 1024))} KB` : ''}
        {e.sha256 ? (
          <>
            {' · SHA-256 '}
            <span style={{ ...num, color: C.textMid }} title={e.sha256}>
              {e.sha256.slice(0, 12)}…
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
}

/**
 * Destroying evidence, on a law enforcement request only (18 U.S.C.
 * 2258B(c)(2)). Global Admin, the request's reference, the report id typed
 * back, and a hold. The rows and hashes stay as the record.
 */
function DestroyEvidence({ report: r, onChange }: { report: ReportDetail; onChange: (r: ReportDetail) => void }) {
  const custodian = useIsCustodian();
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [confirm, setConfirm] = useState('');
  const action = useAction();
  const left = r.evidence.filter((e) => e.kind === 'IMAGE' && !e.destroyedAt).length;
  if (!custodian || left === 0) return null;
  if (!open) {
    return (
      <div>
        <button type="button" onClick={() => setOpen(true)} style={{ background: 'none', border: 0, padding: 0, color: C.dangerText, fontSize: '.82rem', cursor: 'pointer' }}>
          Destroy evidence at a law enforcement request…
        </button>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: '.6rem', padding: '.9rem 1rem', border: `1px solid ${C.dangerBorder}`, borderRadius: 8, background: C.dangerBg }}>
      <p style={{ margin: 0, color: C.dangerText, lineHeight: 1.45 }}>
        Only when a law enforcement agency asks for it to be destroyed. The {left} image{left === 1 ? '' : 's'} go; this report keeps the hashes and
        the request.
      </p>
      <OutcomeNote outcome={action.outcome} />
      <Field label="Law enforcement request reference">
        <input value={reference} onChange={(e) => setReference(e.target.value)} style={inputStyle} maxLength={300} />
      </Field>
      <Field label={`Type ${r.id} to confirm`}>
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} style={{ ...inputStyle, ...num }} />
      </Field>
      <div style={{ display: 'flex', gap: '.6rem' }}>
        <HoldButton
          title="Hold to destroy the images. This cannot be undone."
          disabled={action.running || reference.trim().length < 3 || confirm.trim().toUpperCase() !== r.id}
          onHold={() =>
            void action.run(async () => {
              onChange(await api.destroyEvidence(r.id, reference.trim(), confirm.trim()));
              return 'Destroyed. The record and hashes are kept.';
            })
          }
        >
          {action.running ? 'Working…' : 'Destroy evidence'}
        </HoldButton>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

// ── The viewer ─────────────────────────────────────────────────────────

/**
 * One image, in its own tab: `#/reports/R-0000123/evidence/<id>`.
 *
 * Blurred until the person chooses to reveal it, then shown in full color
 * (Jon, 2026-10-25: no grayscale step). Interactive
 * blur spares reviewers without costing accuracy, where a fixed blur does
 * (Das et al., HCOMP 2020). Under a legal hold the page asks why before it asks
 * for the link, and only a Global Admin gets one. The link lasts 60 seconds and
 * is not shown; the image is held only by this page.
 */
export function EvidenceViewer({ reportId, evidenceId }: { reportId: string; evidenceId: string }) {
  const { state } = useAsync(() => api.report(reportId), [reportId]);
  return (
    <Screen>
      <AsyncBoundary state={state}>{(r) => <Viewer report={r} evidenceId={evidenceId} />}</AsyncBoundary>
    </Screen>
  );
}

function Viewer({ report: r, evidenceId }: { report: ReportDetail; evidenceId: string }) {
  const item = r.evidence.find((e) => e.id === evidenceId);
  const custodian = useIsCustodian();
  const [reason, setReason] = useState('');
  const [link, setLink] = useState<{ url: string; sha256: string | null } | null>(null);
  const [revealed, setRevealed] = useState(false);
  const action = useAction();

  const open = (why?: string) =>
    void action.run(async () => {
      const got = await api.openEvidence(r.id, evidenceId, why);
      setLink({ url: got.url, sha256: got.sha256 });
      return 'Opened. This opening is recorded on the report.';
    });

  // Not under a hold: open at once; the button that brought you here was the choice.
  useEffect(() => {
    if (item && !item.destroyedAt && !r.legalHold) open();
  }, [evidenceId]);

  if (!item || item.kind !== 'IMAGE') return <Note tone="danger">There is no such image on {r.id}.</Note>;

  return (
    <div style={{ display: 'grid', gap: '1rem', maxWidth: 960 }}>
      <header style={{ display: 'grid', gap: '.3rem' }}>
        <a href={`#/reports/${encodeURIComponent(r.id)}`} style={{ fontSize: '.85rem', color: C.textMid2, textDecoration: 'none' }}>
          ← {r.id}
        </a>
        <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 720 }}>
          {item.role === 'avatar' ? 'Profile picture' : 'Reported image'} <span style={{ ...num, color: C.textMid2, fontWeight: 600 }}>{r.id}</span>
        </h1>
        <p style={{ margin: 0, fontSize: '.84rem', color: C.textMid2 }}>
          Captured {stamp(item.capturedAt)} from {r.product} · SHA-256 <span style={num}>{item.sha256}</span>
        </p>
      </header>

      {item.destroyedAt ? (
        <Note>Destroyed {stamp(item.destroyedAt)}, at law enforcement request {item.destroyReference}.</Note>
      ) : r.legalHold && !link ? (
        custodian ? (
          <div style={{ display: 'grid', gap: '.6rem', padding: '1rem', border: `1px solid ${C.dangerBorder}`, borderRadius: 8, background: C.dangerBg }}>
            <p style={{ margin: 0, color: C.dangerText }}>
              {r.id} is under a legal hold ({r.escalationStatus ? STATUS_LABELS[r.escalationStatus] : ''}). Say why you need to open this image; the
              reason is kept with the report.
            </p>
            <OutcomeNote outcome={action.outcome} />
            <Field label="Reason">
              <input value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} maxLength={500} />
            </Field>
            <div>
              <Button tone="danger" disabled={action.running || reason.trim().length < 5} onClick={() => open(reason.trim())}>
                {action.running ? 'Opening…' : 'Open, blurred'}
              </Button>
            </div>
          </div>
        ) : (
          <Note tone="warn">{r.id} is under a legal hold. Only a Global Admin may open its images.</Note>
        )
      ) : !link ? (
        <>
          <OutcomeNote outcome={action.outcome} />
          {action.running ? <Note>Opening…</Note> : null}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <Button onClick={() => setRevealed((v) => !v)}>{revealed ? 'Blur again' : 'Reveal'}</Button>
            <span style={{ fontSize: '.8rem', color: C.textMid2 }}>This opening is recorded on {r.id}. Close the tab when you are done.</span>
          </div>
          <div style={{ background: C.page, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', display: 'grid', placeItems: 'center', minHeight: 240 }}>
            <img
              src={link.url}
              alt={`${item.role === 'avatar' ? 'Profile picture' : 'Reported image'} on ${r.id}`}
              referrerPolicy="no-referrer"
              draggable={false}
              onContextMenu={(ev) => ev.preventDefault()}
              style={{
                maxWidth: '100%',
                maxHeight: '75vh',
                display: 'block',
                filter: revealed ? 'none' : 'blur(22px)',
                transform: revealed ? undefined : 'scale(1.04)',
                transition: 'filter .2s ease',
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
